import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { reservasAPI } from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const generarHoras = (horaApertura = '08:00', horaCierre = '21:00') => {
  const inicio = parseInt(horaApertura.slice(0, 2), 10) || 8;
  const fin = parseInt(horaCierre.slice(0, 2), 10) || 21;

  return Array.from({ length: fin - inicio + 1 }, (_, idx) => {
    const hora = inicio + idx;
    return `${hora.toString().padStart(2, '0')}:00`;
  });
};

const DEFAULT_HORARIO = {
  horas: generarHoras(),
  horaApertura: '08:00',
  horaCierre: '21:00'
};

const DURACION_MAXIMA = 3;

const TIPOS_CANCHA = {
  FUTBOL_7: 'futbol_7',
  FUTBOL_9: 'futbol_9'
};

const TARIFA_FUTBOL_9_PUBLICO = 260000;
const TARIFA_FUTBOL_9_GEMELLI = 230000;

const EXPERIENCIAS_DESTACADAS = [
  {
    kicker: 'Reserva fluida',
    titulo: 'El partido empieza desde una página que se siente premium.',
    descripcion:
      'Una narrativa por escenas guía a las familias desde la disponibilidad hasta el pago, con bloques amplios, contraste suave y movimiento sutil inspirado en lanzamientos de producto.',
    metricas: ['7 días visibles', 'Pago Nequi', 'Aprobación admin'],
    tono: 'from-slate-950 via-emerald-950 to-black'
  },
  {
    kicker: 'Formato flexible',
    titulo: 'Tres canchas F7 o una experiencia F7 + F9 en segundos.',
    descripcion:
      'Los slides explican las configuraciones como momentos de juego: entrenamientos, torneos, partidos familiares y eventos con una lectura clara en móvil y escritorio.',
    metricas: ['3 x F7', 'F7 + F9', '1–3 horas'],
    tono: 'from-emerald-900 via-teal-950 to-slate-950'
  },
  {
    kicker: 'Comunidad Gemelli',
    titulo: 'La cancha conecta deporte, colegio y admisiones.',
    descripcion:
      'La estética tipo Apple mantiene el foco en mensajes grandes, llamados a la acción limpios y tarjetas con profundidad para reforzar confianza.',
    metricas: ['Familias', 'Admisiones', 'Tarifas claras'],
    tono: 'from-zinc-950 via-slate-900 to-emerald-950'
  }
];

const NEQUI_PAYMENT_NUMBER = '3203768582';
const NEQUI_QR_LINK = process.env.REACT_APP_NEQUI_QR_LINK || 'https://wa.me/qr/2HN5E6CD2BRNJ1';
const NEQUI_QR_IMAGE_URL =
  process.env.REACT_APP_NEQUI_QR_IMAGE_URL ||
  'https://chart.googleapis.com/chart?chs=600x600&cht=qr&chl=https%3A%2F%2Fwa.me%2Fqr%2F2HN5E6CD2BRNJ1';
const NEQUI_QR_IMAGE_FALLBACK_URL =
  process.env.REACT_APP_NEQUI_QR_IMAGE_FALLBACK_URL ||
  'https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=https%3A%2F%2Fwa.me%2Fqr%2F2HN5E6CD2BRNJ1';
const ADMISIONES_GEMELLI_URL = 'https://admisiones-colgemelli.vercel.app/';

const parseFechaLocal = (fecha) => {
  if (!fecha) return new Date();
  return new Date(`${fecha}T00:00:00`);
};

const obtenerHorasSeleccionadas = (horaInicio, duracion, horario = []) => {
  if (!horaInicio || !duracion || duracion < 1) return [];

  const indiceInicio = horario.indexOf(horaInicio);
  if (indiceInicio === -1) return [];

  return horario.slice(indiceInicio, indiceInicio + duracion);
};

const formatearRangoHoras = (horas = []) => {
  if (!horas.length) return '';
  if (horas.length === 1) return horas[0];
  return `${horas[0]} - ${horas[horas.length - 1]}`;
};

const esFinDeSemana = (fecha) => {
  const fechaBase = parseFechaLocal(fecha);
  const diaSemana = fechaBase.getDay();
  return diaSemana === 0 || diaSemana === 6;
};

const obtenerTarifaPorHora = (hora, fecha, esFamiliaGemellista = false, tipoCancha = TIPOS_CANCHA.FUTBOL_7) => {
  if (tipoCancha === TIPOS_CANCHA.FUTBOL_9) {
    return esFamiliaGemellista ? TARIFA_FUTBOL_9_GEMELLI : TARIFA_FUTBOL_9_PUBLICO;
  }

  const horaEntera = parseInt(hora?.slice(0, 2), 10);

  if (Number.isNaN(horaEntera)) return 0;

  if (esFamiliaGemellista) {
    return horaEntera >= 17 ? 110000 : 90000;
  }
  
  if (esFinDeSemana(fecha)) return 130000;

  return horaEntera >= 17 ? 130000 : 100000;
};

const moverALunesSiguiente = (fecha) => {
  const diaSemana = fecha.getDay();
  const diasHastaLunes = diaSemana === 1 ? 0 : (8 - diaSemana) % 7;
  const fechaLunes = new Date(fecha);
  fechaLunes.setDate(fechaLunes.getDate() + diasHastaLunes);
  return fechaLunes;
};

const calcularPascua = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, mes, dia);
};

const formatearFechaISO = (fecha) => format(fecha, 'yyyy-MM-dd');

const obtenerFestivosColombia = (year) => {
  const pascua = calcularPascua(year);

  const festivosFijos = [
    new Date(year, 0, 1),
    new Date(year, 4, 1),
    new Date(year, 6, 20),
    new Date(year, 7, 7),
    new Date(year, 11, 8),
    new Date(year, 11, 25)
  ];

  const festivosEmiliani = [
    new Date(year, 0, 6),
    new Date(year, 2, 19),
    new Date(year, 5, 29),
    new Date(year, 7, 15),
    new Date(year, 9, 12),
    new Date(year, 10, 1),
    new Date(year, 10, 11)
  ].map(moverALunesSiguiente);

  const festivosSemanaSanta = [
    new Date(pascua.getTime() - 3 * 24 * 60 * 60 * 1000),
    new Date(pascua.getTime() - 2 * 24 * 60 * 60 * 1000)
  ];

  const festivosMoviles = [
    moverALunesSiguiente(new Date(pascua.getTime() + 43 * 24 * 60 * 60 * 1000)),
    moverALunesSiguiente(new Date(pascua.getTime() + 64 * 24 * 60 * 60 * 1000)),
    moverALunesSiguiente(new Date(pascua.getTime() + 71 * 24 * 60 * 60 * 1000))
  ];

  return new Set([
    ...festivosFijos,
    ...festivosEmiliani,
    ...festivosSemanaSanta,
    ...festivosMoviles
  ].map(formatearFechaISO));
};

const esFestivoColombia = (fechaISO) => {
  if (!fechaISO) return false;
  const fecha = parseFechaLocal(fechaISO);
  const festivos = obtenerFestivosColombia(fecha.getFullYear());
  return festivos.has(fechaISO);
};

const calcularTotalReserva = (horas = [], fecha, esFamiliaGemellista = false, tipoCancha = TIPOS_CANCHA.FUTBOL_7) =>
  (horas || []).reduce(
    (total, hora) => total + obtenerTarifaPorHora(hora, fecha, esFamiliaGemellista, tipoCancha),
    0
  );

const formatearCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0
});

const obtenerHoraInicialDisponible = (duracion, horario = [], horasLibres = []) => {
  if (!duracion || duracion < 1) return '';

  return (horario || []).find((horaSlot) => {
    const rango = obtenerHorasSeleccionadas(horaSlot, duracion, horario);
    return rango.length === duracion && rango.every((hora) => horasLibres.includes(hora));
  }) || '';
};

const Home = () => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [formData, setFormData] = useState({
    nombre_cliente: '',
    email_cliente: '',
    celular_cliente: '',
    es_familia_gemellista: false,
    tipo_cancha: TIPOS_CANCHA.FUTBOL_7,
    nombre_gemellista: '',
    cedula_gemellista: '',
    fecha: today,
    hora: DEFAULT_HORARIO.horas[0],
    duracion: 1
  });
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [horasDisponibles, setHorasDisponibles] = useState(DEFAULT_HORARIO.horas);
  const [horasOcupadas, setHorasOcupadas] = useState([]);
  const [horasPendientes, setHorasPendientes] = useState([]);
  const [horarioDelDia, setHorarioDelDia] = useState(DEFAULT_HORARIO);
  const [consultando, setConsultando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [disponibilidadDias, setDisponibilidadDias] = useState([]);
  const [cargandoDias, setCargandoDias] = useState(true);
  const [errorDias, setErrorDias] = useState('');
  const [resumenReserva, setResumenReserva] = useState(null);
  const [qrImageSrc, setQrImageSrc] = useState(NEQUI_QR_IMAGE_URL);
  const cacheKeyDisponibilidad = useCallback(
    (fecha, tipoCancha = formData.tipo_cancha) => `${tipoCancha}:${fecha}`,
    [formData.tipo_cancha]
  );
  const [disponibilidadCache, setDisponibilidadCache] = useState({
    [`${TIPOS_CANCHA.FUTBOL_7}:${today}`]: {
      horasDisponibles: DEFAULT_HORARIO.horas,
      horasOcupadas: [],
      horasPendientes: [],
      horario: DEFAULT_HORARIO,
      error: ''
    }
  });
  
  const horasValidasParaDuracion = (horarioDelDia.horas || []).filter((horaSlot) => {
    const rango = obtenerHorasSeleccionadas(horaSlot, formData.duracion, horarioDelDia.horas);
    return rango.length === formData.duracion && rango.every((hora) => horasDisponibles.includes(hora));
  });

  const horasSeleccionadas = obtenerHorasSeleccionadas(
    formData.hora,
    formData.duracion,
    horarioDelDia.horas
  );

  const horasParaPago = resumenReserva?.horas || horasSeleccionadas;
  const fechaParaPago = resumenReserva?.fecha || formData.fecha;
  const esFamiliaGemellista = resumenReserva?.es_familia_gemellista || formData.es_familia_gemellista;
  const tipoCanchaReserva = resumenReserva?.tipo_cancha || formData.tipo_cancha;
  const estadoGemellistaActual =
    resumenReserva?.estado_gemellista || (formData.es_familia_gemellista ? 'Pendiente' : 'No aplica');
  const tieneTarifaGemellistaActiva = estadoGemellistaActual === 'Aprobado';
  const totalResumenReserva = calcularTotalReserva(
    horasParaPago,
    fechaParaPago,
    tieneTarifaGemellistaActiva,
    tipoCanchaReserva
  );
  const esDiaFestivoSeleccionado = esFestivoColombia(formData.fecha);
  const fechaReservaFormateada = resumenReserva?.fecha
    ? format(parseFechaLocal(resumenReserva.fecha), "EEEE d 'de' MMMM, yyyy", { locale: es })
    : 'Fecha por confirmar';
  
  useEffect(() => {
    const horaSugerida = obtenerHoraInicialDisponible(formData.duracion, horarioDelDia.horas, horasDisponibles);

    if (formData.hora && horasValidasParaDuracion.includes(formData.hora)) return;

    setFormData((prev) => ({
      ...prev,
      hora: horaSugerida || prev.hora
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.duracion, horasDisponibles, horarioDelDia.horas]);
  
  const cargarDisponibilidad = async (fecha) => {
    try {
      setConsultando(true);
      const response = await reservasAPI.obtenerDisponibilidad(fecha, { tipo_cancha: formData.tipo_cancha });
      const horasOcupadasAPI = response.data.horasOcupadas || [];
      const horasPendientesAPI = response.data.horasPendientes || [];
      const horario = response.data.horario?.horas?.length
        ? {
            horas: response.data.horario.horas,
            horaApertura: response.data.horario.horaApertura,
            horaCierre: response.data.horario.horaCierre
          }
        : DEFAULT_HORARIO;

      const horas = horario.horas?.length ? horario.horas : DEFAULT_HORARIO.horas;
      const horasLibres = horas.filter((hora) => !horasOcupadasAPI.includes(hora));
      const horasSeleccionables = horasLibres.length ? horasLibres : horas;

      setHorasDisponibles(horasSeleccionables);
      setHorasOcupadas(horasOcupadasAPI);
      setHorasPendientes(horasPendientesAPI);
      setHorarioDelDia(horario);

      setDisponibilidadCache((prev) => ({
        ...prev,
        [cacheKeyDisponibilidad(fecha)]: {
          horasDisponibles: horasSeleccionables,
          horasOcupadas: horasOcupadasAPI,
          horasPendientes: horasPendientesAPI,
          horario,
          error: ''
        }
      }));
      
      const horaInicialSugerida = obtenerHoraInicialDisponible(
        formData.duracion,
        horario.horas,
        horasSeleccionables
      );

      setFormData((prev) => {
        const duracion = Math.min(prev.duracion, DURACION_MAXIMA);
        const rangoActual = obtenerHorasSeleccionadas(prev.hora, duracion, horario.horas);
        const esRangoValido =
          rangoActual.length === duracion && rangoActual.every((hora) => horasSeleccionables.includes(hora));
        const horaPorDefecto = horaInicialSugerida || horasSeleccionables[0] || '';
        return {
          ...prev,
          hora: esRangoValido ? prev.hora : horaPorDefecto,
          duracion
        };
      });
    } catch (error) {
      // Si no se puede consultar la API, mostramos todos los horarios para evitar dejar la UI vacía
      setHorasDisponibles(DEFAULT_HORARIO.horas);
      setHorasOcupadas([]);
      setHorarioDelDia(DEFAULT_HORARIO);
      setHorasPendientes([]);
      setFormData((prev) => ({
        ...prev,
        hora: DEFAULT_HORARIO.horas.includes(prev.hora) ? prev.hora : DEFAULT_HORARIO.horas[0],
        duracion: Math.min(prev.duracion, DURACION_MAXIMA)
      }));
      setDisponibilidadCache((prev) => ({
        ...prev,
        [cacheKeyDisponibilidad(fecha)]: {
          horasDisponibles: DEFAULT_HORARIO.horas,
          horasOcupadas: [],
          horasPendientes: [],
          horario: DEFAULT_HORARIO,
          error: error.response?.data?.error || 'Sin datos de disponibilidad'
        }
      }));
    } finally {
      setConsultando(false);
    }
  };

  useEffect(() => {
    cargarDisponibilidad(formData.fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fecha, formData.tipo_cancha]);

  useEffect(() => {
    let cancelado = false;
    
    const cargarDisponibilidadSemanal = async () => {
      setCargandoDias(true);
      setErrorDias('');

      const dias = Array.from({ length: 7 }, (_, idx) => {
        const fecha = new Date();
        fecha.setDate(fecha.getDate() + idx);
        return {
          fechaValor: format(fecha, 'yyyy-MM-dd'),
          etiqueta: format(fecha, "EEEE d 'de' MMMM", { locale: es })
        };
      });

      try {
        const resultados = await Promise.all(
          dias.map(async (dia) => {
                    const cacheDia = disponibilidadCache[cacheKeyDisponibilidad(dia.fechaValor)];
                if (cacheDia) {
                  return { ...dia, ...cacheDia };
                }
            
            try {
              const response = await reservasAPI.obtenerDisponibilidad(dia.fechaValor, { tipo_cancha: formData.tipo_cancha });
             const horario = response.data?.horario?.horas?.length
                ? {
                    horas: response.data.horario.horas,
                    horaApertura: response.data.horario.horaApertura,
                    horaCierre: response.data.horario.horaCierre
                  }
                : DEFAULT_HORARIO;
              
              const diaData = {
                horasDisponibles: response.data?.horasDisponibles || horario.horas,
                horasOcupadas: response.data?.horasOcupadas || [],
                horasPendientes: response.data?.horasPendientes || [],
                horario,
                error: ''
              };

              setDisponibilidadCache((prev) => ({
                ...prev,
                [cacheKeyDisponibilidad(dia.fechaValor)]: diaData
              }));

              return { ...dia, ...diaData };
            } catch (error) {
              const diaData = {
                horasDisponibles: DEFAULT_HORARIO.horas,
                horasOcupadas: [],
                horasPendientes: [],
                horario: DEFAULT_HORARIO,
                error: error.response?.data?.error || 'Sin datos de disponibilidad'
              };
                        
              setDisponibilidadCache((prev) => ({
                ...prev,
                [cacheKeyDisponibilidad(dia.fechaValor)]: diaData
              }));

              return { ...dia, ...diaData };
            }
          })
        );
       if (!cancelado) {
          setDisponibilidadDias(resultados);
        }
      } catch (error) {
        if (!cancelado) {
          setErrorDias('No pudimos cargar la disponibilidad por día.');
        }
      } finally {
        if (!cancelado) {
          setCargandoDias(false);
        }
      }
    };

    const timeout = setTimeout(() => {
      if (!cancelado) {
        cargarDisponibilidadSemanal();
      }
    }, 200);

    return () => {
      cancelado = true;
      clearTimeout(timeout);
    };
  }, [cacheKeyDisponibilidad, disponibilidadCache, formData.tipo_cancha]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje({ tipo: '', texto: '' });

   const horasSeleccionadas = obtenerHorasSeleccionadas(
      formData.hora,
      formData.duracion,
      horarioDelDia.horas
    );

    if (horasSeleccionadas.length !== formData.duracion) {
      setMensaje({
        tipo: 'error',
        texto: 'Selecciona un bloque de horas consecutivas disponibles'
      });
      return;
    }

    const rangoDisponible = horasSeleccionadas.every((hora) => horasDisponibles.includes(hora));

    if (!rangoDisponible) {
      setMensaje({
        tipo: 'error',
        texto: 'Alguna de las horas ya no está disponible. Elige otro bloque.'
      });
      return;
    }

    if (formData.es_familia_gemellista) {
      if (!formData.nombre_gemellista.trim() || !formData.cedula_gemellista.trim()) {
        setMensaje({
          tipo: 'error',
          texto: 'Ingresa el nombre y la cédula de la Familia Gemellista para aplicar la tarifa especial.'
        });
        return;
      }
    }
    
    try {
      setEnviando(true);
      const response = await reservasAPI.crear({ ...formData, horas: horasSeleccionadas });
      const reservasCreadas = response.data?.reservas || [];
      const reservaBase = reservasCreadas[0] || formData;
      const estadoGemellistaRespuesta =
        reservaBase.estado_gemellista || (reservaBase.es_familia_gemellista ? 'Pendiente' : 'No aplica');
      const horasConfirmadas = reservasCreadas.length
        ? reservasCreadas.map((reserva) => reserva.hora)
        : horasSeleccionadas;
      const horasOrdenadas = [...horasConfirmadas].sort(
        (a, b) => (horarioDelDia.horas || []).indexOf(a) - (horarioDelDia.horas || []).indexOf(b)
      );
      const fechaLegible = format(parseFechaLocal(formData.fecha), "dd 'de' MMMM, yyyy", { locale: es });
      const texto = `¡Reserva creada! Tu solicitud quedó pendiente de aprobación del administrador o la cancha. Te esperamos el ${fechaLegible} de ${formatearRangoHoras(horasOrdenadas)}.`;
      setMensaje({ tipo: 'exito', texto });
      setResumenReserva({
        nombre_cliente: reservaBase.nombre_cliente,
        email_cliente: reservaBase.email_cliente,
        celular_cliente: reservaBase.celular_cliente,
        es_familia_gemellista: reservaBase.es_familia_gemellista,
        tipo_cancha: reservaBase.tipo_cancha || formData.tipo_cancha,
        nombre_gemellista: reservaBase.nombre_gemellista,
        cedula_gemellista: reservaBase.cedula_gemellista,
        estado_gemellista: estadoGemellistaRespuesta,
        fecha: reservaBase.fecha,
        horas: horasOrdenadas
      });
      setFormData({
        nombre_cliente: '',
        email_cliente: '',
        celular_cliente: '',
        es_familia_gemellista: false,
        tipo_cancha: TIPOS_CANCHA.FUTBOL_7,
        nombre_gemellista: '',
        cedula_gemellista: '',
        fecha: formData.fecha,
        hora: obtenerHoraInicialDisponible(1, horarioDelDia.horas, horasDisponibles),
        duracion: 1
      });
      
      setHorasPendientes((prev) => {
        const nuevasPendientes = new Set(prev);
        horasSeleccionadas.forEach((hora) => nuevasPendientes.add(hora));
        return Array.from(nuevasPendientes);
      });

      setDisponibilidadDias((prev) =>
        prev.map((dia) =>
          dia.fechaValor === formData.fecha
            ? {
                ...dia,
                horasDisponibles: dia.horasDisponibles,
                horasOcupadas: dia.horasOcupadas || [],
                horasPendientes: [...(dia.horasPendientes || []), ...horasSeleccionadas]
              }
            : dia
        )
      );
      
      cargarDisponibilidad(formData.fecha);
      return response;
    } catch (error) {
      const mensajeError =
        error.response?.data?.error ||
        (error.code === 'ERR_NETWORK'
          ? 'No pudimos conectar con el servidor en este momento. Intenta nuevamente en unos minutos o contáctanos si el problema continúa.'
          : error.message);
      
      setMensaje({
        tipo: 'error',
        texto: mensajeError || 'No pudimos crear la reserva'
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] text-[#1d1d1f]">
    {resumenReserva && (
        <div
          className="fixed inset-0 z-20 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 px-4 py-6 sm:items-center"
          onClick={() => setResumenReserva(null)}
        >
          <div
            className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setResumenReserva(null)}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
              aria-label="Cerrar resumen de reserva"
            >
              ✕
            </button>
            <div className="grid gap-6 p-6 md:p-8 md:grid-cols-2">
              <div>
                <p className="text-sm font-semibold uppercase text-primary">Reserva creada</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-800">Detalles de tu reserva</h2>
                <div className="mt-4 space-y-3 rounded-xl bg-gray-50 p-4">
                  <div>
                    <p className="text-xs uppercase text-gray-500">Día</p>
                    <p className="text-lg font-semibold text-gray-800">{fechaReservaFormateada}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-gray-500">Cancha</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {tipoCanchaReserva === TIPOS_CANCHA.FUTBOL_9 ? 'Fútbol 9' : 'Fútbol 7'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500">Hora</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {formatearRangoHoras(resumenReserva.horas || [])}
                      </p>
                    </div>
                  </div>
                  <div>
                      <p className="text-xs uppercase text-gray-500">Estado</p>
                      <p className="text-sm font-semibold text-blue-700">Pendiente de aprobación</p>
                      <p className="mt-1 text-xs text-gray-600">
                        Un administrador o encargado de cancha debe aprobar tu reserva antes de confirmarla.
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Realiza el pago con 2 días de anticipación a la fecha señalada ({fechaReservaFormateada}).
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs uppercase text-gray-500">Reservado por</p>
                    <p className="text-lg font-semibold text-gray-800">{resumenReserva.nombre_cliente}</p>
                    <p className="text-sm text-gray-600">{resumenReserva.email_cliente}</p>
                    <p className="text-sm text-gray-600">{resumenReserva.celular_cliente}</p>
                </div>
                {resumenReserva.es_familia_gemellista && (
                  <div className="rounded-lg border border-primary/40 bg-white p-3">
                    <p className="text-xs uppercase text-primary font-semibold">Tarifa Familia Gemellista</p>
                    <p className="text-sm text-gray-700">
                      Nombre registrado: <span className="font-semibold">{resumenReserva.nombre_gemellista}</span>
                    </p>
                    <p className="text-sm text-gray-700">
                      Cédula: <span className="font-semibold">{resumenReserva.cedula_gemellista}</span>
                    </p>
                    <p className="text-sm text-gray-700">
                      Estado de verificación:{' '}
                      <span className="font-semibold capitalize">
                        {resumenReserva.estado_gemellista || 'Pendiente'}
                      </span>
                    </p>
                    {resumenReserva.estado_gemellista !== 'Aprobado' && (
                      <p className="mt-1 text-xs text-blue-700">
                        Mientras se aprueba, la reserva se cobra con la tarifa general.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-green-50 p-4 shadow-inner">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Paga tu reserva</p>
                    <p className="text-base font-bold text-gray-800">Nequi • Número {NEQUI_PAYMENT_NUMBER}</p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary shadow">Prioritario</span>
                </div>
                <div className="mt-4 rounded-lg bg-white p-4 shadow">
                  <div className="flex items-start gap-3">
                    <div>
                      <p className="text-xs uppercase text-gray-500">Total a pagar</p>
                      <p className="text-2xl font-bold text-primary">
                        {horasParaPago.length ? formatearCOP.format(totalResumenReserva) : 'Selecciona horario'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {horasParaPago.length
                          ? esFamiliaGemellista
                            ? `${horasParaPago.length} ${horasParaPago.length === 1 ? 'hora' : 'horas'} con tarifa especial ${tipoCanchaReserva === TIPOS_CANCHA.FUTBOL_9 ? 'Papás Gemelli fútbol 9' : 'Familia Gemellista'}.`
                            : `${horasParaPago.length} ${horasParaPago.length === 1 ? 'hora' : 'horas'} calculadas automáticamente según la tarifa vigente.`
                          : 'Calcularemos el total cuando confirmes tu horario.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-col items-center gap-3 rounded-lg bg-white p-4 shadow">
                  <img
                    src={qrImageSrc}
                    alt="Código QR para pagar tu reserva con Nequi"
                    className="h-48 w-48 rounded-lg border border-gray-200 object-contain"
                      onError={() => {
                      if (qrImageSrc !== NEQUI_QR_IMAGE_FALLBACK_URL) {
                        setQrImageSrc(NEQUI_QR_IMAGE_FALLBACK_URL);
                      }
                    }}
                  />
                  <p className="text-center text-sm text-gray-700">
                    Escanea el código o usa el número <span className="font-semibold">{NEQUI_PAYMENT_NUMBER}</span> para
                    completar tu pago por Nequi.
                  </p>
                  <a
                    href={NEQUI_QR_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-green-600"
                  >
                    Habla con nosotros
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <nav className="mb-8 flex items-center justify-between rounded-full border border-white/70 bg-white/80 px-5 py-3 shadow-sm backdrop-blur-xl">
          <span className="text-sm font-semibold tracking-tight text-gray-900">Sintética Colgemelli</span>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-black"
          >
            Administración
          </Link>
        </nav>

        <header className="apple-reveal overflow-hidden rounded-[2rem] bg-white shadow-sm">
          <div className="grid items-center gap-10 px-6 py-12 md:grid-cols-[1.05fr_0.95fr] md:px-12 lg:px-16 lg:py-16">
            <div className="text-center md:text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">Reserva pública</p>
              <h1 className="mt-4 text-5xl font-semibold tracking-[-0.05em] text-gray-950 md:text-6xl lg:text-7xl">
                Tres canchas. Una experiencia más grande.
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600 md:mx-0">
                Ahora puedes jugar en tres canchas de fútbol 7 o transformar el espacio en una cancha de fútbol 7 y una de fútbol 9. Reserva tu horario con una experiencia simple, clara y premium.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row md:justify-start">
                <a
                  href="#reservar"
                  className="rounded-full bg-primary px-6 py-3 text-base font-semibold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-600"
                >
                  Reservar ahora
                </a>
                <Link
                  to="/como-llegar"
                  className="rounded-full border border-primary/30 bg-white px-6 py-3 text-base font-semibold text-primary transition hover:border-primary hover:bg-emerald-50"
                >
                  Cómo llegar
                </Link>
              </div>
            </div>
            <div className="hero-device relative min-h-[360px] overflow-hidden rounded-[2rem] bg-gray-950 p-6 text-white shadow-2xl">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.45),transparent_30%),radial-gradient(circle_at_80%_0%,rgba(59,130,246,0.38),transparent_25%)]" />
              <div className="relative flex h-full min-h-[320px] flex-col justify-between rounded-[1.5rem] border border-white/15 bg-white/10 p-5 backdrop-blur">
                <div className="flex items-center justify-between text-sm text-white/80">
                  <span>Colgemelli Sports Hub</span>
                  <span>F7 · F9</span>
                </div>
                <div className="grid gap-3">
                  <div className="h-24 rounded-2xl border border-white/30 bg-emerald-400/25 shadow-inner" />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-24 rounded-2xl border border-white/30 bg-emerald-400/20 shadow-inner" />
                    <div className="h-24 rounded-2xl border border-white/30 bg-blue-400/20 shadow-inner" />
                  </div>
                </div>
                <p className="text-3xl font-semibold tracking-tight">3 canchas F7 o formato F7 + F9.</p>
              </div>
            </div>
          </div>
        </header>

        <section className="apple-reveal mt-6 rounded-[2rem] bg-[#fbfbfd] px-4 py-10 shadow-sm md:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary">Una mirada más cercana</p>
            <h2 className="mt-4 text-4xl font-semibold tracking-[-0.045em] text-gray-950 md:text-6xl">
              Slides grandes, movimiento suave y toda la información sin fricción.
            </h2>
          </div>
          <div className="premium-slider mt-10 flex snap-x gap-5 overflow-x-auto pb-4">
            {EXPERIENCIAS_DESTACADAS.map((slide, index) => (
              <article
                key={slide.titulo}
                className={`premium-slide min-w-[86%] snap-center overflow-hidden rounded-[2rem] bg-gradient-to-br ${slide.tono} p-6 text-white shadow-2xl sm:min-w-[68%] lg:min-w-[42%]`}
                style={{ '--slide-delay': `${index * 140}ms` }}
              >
                <div className="flex min-h-[430px] flex-col justify-between">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-200">{slide.kicker}</p>
                    <h3 className="mt-5 text-4xl font-semibold tracking-[-0.04em] md:text-5xl">{slide.titulo}</h3>
                    <p className="mt-5 max-w-xl text-lg leading-8 text-white/76">{slide.descripcion}</p>
                  </div>
                  <div className="mt-10 grid gap-3 sm:grid-cols-3">
                    {slide.metricas.map((metrica) => (
                      <span key={metrica} className="rounded-full border border-white/15 bg-white/10 px-4 py-3 text-center text-sm font-semibold backdrop-blur">
                        {metrica}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="apple-reveal mt-6 overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-slate-950 via-emerald-950 to-primary p-6 text-white shadow-xl md:p-8">
          <div className="grid items-center gap-6 md:grid-cols-[1.15fr_0.85fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-200">
                Conoce el Colegio Gemelli
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                Si te gustó la cancha, ahora te va a encantar el Gemelli.
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-7 text-white/85 md:text-lg">
                Detrás de esta experiencia deportiva hay una comunidad educativa que forma con cercanía,
                valores y espacios pensados para crecer. Mira nuestra información de admisiones y descubre
                por qué más familias están eligiendo vivir la experiencia Gemelli desde el aula hasta la cancha.
              </p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-5 shadow-inner backdrop-blur">
              <p className="text-sm font-semibold text-emerald-100">Agenda el siguiente paso</p>
              <p className="mt-2 text-sm leading-6 text-white/80">
                Explora requisitos, beneficios y canales de contacto para iniciar tu proceso de admisión.
              </p>
              <a
                href={ADMISIONES_GEMELLI_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-bold text-emerald-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-emerald-50"
              >
                Ver información de admisiones
              </a>
            </div>
          </div>
        </section>

        <section className="apple-reveal mt-6 rounded-[1.75rem] bg-white p-6 shadow-sm md:p-8">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Horarios y tarifas vigentes</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-gray-950">Precios claros antes de jugar.</h2>
            </div>
            <div className="grid gap-4 text-sm text-gray-700 sm:grid-cols-2">
              <div className="rounded-2xl bg-gray-50 p-4">
                <p className="font-semibold text-gray-950">Público general</p>
                <p className="mt-2">Lunes a viernes desde 4:00 p.m.: $130.000 COP por hora.</p>
                <p className="mt-1">Sábados, domingos y festivos: $130.000 COP por hora todo el día.</p>
              </div>
              <div className="rounded-2xl bg-emerald-50 p-4">
                <p className="font-semibold text-gray-950">Familia Gemellista</p>
                <p className="mt-2">Lunes a domingo desde 4:00 p.m.: $110.000 COP por hora.</p>
                <p className="mt-1">La tarifa especial se aplica después de validación administrativa.</p>
              </div>
              <div className="rounded-2xl bg-slate-950 p-4 text-white sm:col-span-2">
                <p className="font-semibold">Cancha fútbol 9</p>
                <p className="mt-2 text-white/80">Público fútbol 9: $260.000 COP por hora.</p>
                <p className="mt-1 text-white/80">Papás Gemelli: $230.000 COP por hora, sujeto a validación.</p>
              </div>
            </div>
          </div>
        </section>
              
        <div id="reservar" className="apple-reveal mt-8 grid md:grid-cols-2 gap-8 items-start">
          <div className="rounded-[1.75rem] bg-white p-6 shadow-sm md:p-8 order-1">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Reservar un horario</h2>

            {mensaje.texto && (
              <div
                className={`mb-6 p-4 rounded-lg border ${
                  mensaje.tipo === 'exito'
                    ? 'bg-green-50 text-green-800 border-green-200'
                    : 'bg-red-50 text-red-800 border-red-200'
                }`}
              >
                {mensaje.texto}
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Nombre y apellido</label>
                  <input
                    type="text"
                    required
                    value={formData.nombre_cliente}
                    onChange={(e) => setFormData({ ...formData, nombre_cliente: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Juan Pérez"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Correo electrónico</label>
                  <input
                    type="email"
                    required
                    value={formData.email_cliente}
                    onChange={(e) => setFormData({ ...formData, email_cliente: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="tu@correo.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Número de contacto</label>
                  <input
                    type="tel"
                    required
                    value={formData.celular_cliente}
                    onChange={(e) => setFormData({ ...formData, celular_cliente: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="300 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Fecha</label>
                  <input
                    type="date"
                    required
                    min={today}
                    value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-gray-800 font-semibold">Elige el tipo de cancha</p>
                <p className="mt-1 text-sm text-gray-600">El valor y la disponibilidad se calculan según tu elección y la capacidad simultánea de las canchas.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className={`cursor-pointer rounded-xl border p-4 transition ${formData.tipo_cancha === TIPOS_CANCHA.FUTBOL_7 ? 'border-primary bg-green-50 ring-2 ring-primary/20' : 'border-gray-200 bg-white hover:border-primary'}`}>
                    <input
                      type="radio"
                      name="tipo_cancha"
                      value={TIPOS_CANCHA.FUTBOL_7}
                      checked={formData.tipo_cancha === TIPOS_CANCHA.FUTBOL_7}
                      onChange={(e) => setFormData({ ...formData, tipo_cancha: e.target.value })}
                      className="sr-only"
                    />
                    <span className="block font-semibold text-gray-900">Fútbol 7</span>
                    <span className="mt-1 block text-sm text-gray-600">Tarifa por hora según horario vigente.</span>
                  </label>
                  <label className={`cursor-pointer rounded-xl border p-4 transition ${formData.tipo_cancha === TIPOS_CANCHA.FUTBOL_9 ? 'border-primary bg-green-50 ring-2 ring-primary/20' : 'border-gray-200 bg-white hover:border-primary'}`}>
                    <input
                      type="radio"
                      name="tipo_cancha"
                      value={TIPOS_CANCHA.FUTBOL_9}
                      checked={formData.tipo_cancha === TIPOS_CANCHA.FUTBOL_9}
                      onChange={(e) => setFormData({ ...formData, tipo_cancha: e.target.value })}
                      className="sr-only"
                    />
                    <span className="block font-semibold text-gray-900">Fútbol 9</span>
                    <span className="mt-1 block text-sm text-gray-600">Público $260.000 · Papás Gemelli $230.000 por hora.</span>
                  </label>
                </div>
              </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-gray-800 font-semibold">¿Eres parte de la Familia Gemellista?</p>
                    <p className="text-sm text-gray-600">
                     Si lo eres, comparte tu nombre y cédula. Un administrador validará la información y, si es aprobado,
                      aplicaremos la tarifa especial.
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="radio"
                        name="familia_gemellista"
                        checked={!formData.es_familia_gemellista}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            es_familia_gemellista: false,
                            nombre_gemellista: '',
                            cedula_gemellista: ''
                          }))
                        }
                        className="h-4 w-4 text-primary"
                      />
                      No
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="radio"
                        name="familia_gemellista"
                        checked={formData.es_familia_gemellista}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            es_familia_gemellista: true
                          }))
                        }
                        className="h-4 w-4 text-primary"
                      />
                      Sí, soy Familia Gemellista
                    </label>
                  </div>
                </div>

                {formData.es_familia_gemellista && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Nombre registrado</label>
                      <input
                        type="text"
                        required={formData.es_familia_gemellista}
                        value={formData.nombre_gemellista}
                        onChange={(e) => setFormData({ ...formData, nombre_gemellista: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Nombre completo del familiar"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Cédula</label>
                      <input
                        type="text"
                        required={formData.es_familia_gemellista}
                        value={formData.cedula_gemellista}
                        onChange={(e) => setFormData({ ...formData, cedula_gemellista: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Número de documento"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                )}
              </div>
             <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2 flex items-center justify-between">
                    <span>Hora de inicio</span>
                    {consultando && <span className="text-sm text-gray-500">Actualizando disponibilidad...</span>}
                  </label>
                  <select
                    required
                    value={formData.hora}
                    onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={consultando || horasDisponibles.length === 0}
                  >
                    <option value="" disabled>
                      {consultando ? 'Consultando horarios...' : 'Selecciona un horario'}
                    </option>
                  {horasValidasParaDuracion.map((hora) => (
                      <option key={hora} value={hora}>
                        {hora}
                      </option>
                    ))}
                  </select>
                  {horasValidasParaDuracion.length === 0 && !consultando && (
                    <p className="text-sm text-red-600 mt-2">No hay bloques consecutivos disponibles para esta duración.</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Duración</label>
                  <select
                    value={formData.duracion}
                    onChange={(e) => {
                      const nuevaDuracion = Math.min(parseInt(e.target.value, 10) || 1, DURACION_MAXIMA);
                      const horasValidasNuevaDuracion = (horarioDelDia.horas || []).filter((horaSlot) => {
                        const rango = obtenerHorasSeleccionadas(horaSlot, nuevaDuracion, horarioDelDia.horas);
                        return rango.length === nuevaDuracion && rango.every((hora) => horasDisponibles.includes(hora));
                      });
                      const horaSugerida = obtenerHoraInicialDisponible(
                        nuevaDuracion,
                        horarioDelDia.horas,
                        horasDisponibles
                      );

                      setFormData((prev) => ({
                        ...prev,
                        duracion: nuevaDuracion,
                        hora: prev.hora && horasValidasNuevaDuracion.includes(prev.hora) ? prev.hora : horaSugerida
                      }));
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={consultando || horasDisponibles.length === 0}
                  >
                    {Array.from({ length: DURACION_MAXIMA }, (_, idx) => idx + 1).map((duracion) => (
                      <option key={duracion} value={duracion}>
                        {duracion} hora{duracion > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">Selecciona hasta {DURACION_MAXIMA} horas consecutivas; cada hora puede tener varias reservas si aún hay cancha disponible.</p>
                </div>
              </div>

            <button
                type="submit"
                disabled={
                  enviando ||
                  consultando ||
                  horasDisponibles.length === 0 ||
                  horasValidasParaDuracion.length === 0
                }
                className="w-full rounded-full bg-primary py-3 font-semibold text-white shadow-lg shadow-emerald-100 transition hover:bg-emerald-600 disabled:bg-gray-400"
              >
                {enviando ? 'Reservando...' : `Reservar ${formData.duracion} hora${formData.duracion > 1 ? 's' : ''}` }
              </button>
            </form>
          </div>

          <div className="rounded-[1.75rem] bg-white p-6 shadow-sm md:p-8 order-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Disponibilidad</p>
                <h3 className="text-xl font-semibold text-gray-800">
                  {format(parseFechaLocal(formData.fecha), "EEEE d 'de' MMMM", { locale: es })}
                </h3>
                {esDiaFestivoSeleccionado && (
                  <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                    <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden />
                    Festivo en Colombia
                  </span>
                )}
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                Abierto {horarioDelDia.horaApertura} - {horarioDelDia.horaCierre}
              </span>
            </div>

             <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(horarioDelDia.horas || []).map((horaSlot) => {
                  const estaReservada = horasOcupadas.includes(horaSlot);
                  const estaPendiente = horasPendientes.includes(horaSlot);
                  const disponible = horasDisponibles.includes(horaSlot) && !estaReservada;
                  const rangoParaHora = obtenerHorasSeleccionadas(
                    horaSlot,
                    formData.duracion,
                    horarioDelDia.horas
                  );
                  const bloqueDisponible =
                    rangoParaHora.length === formData.duracion &&
                    rangoParaHora.every((hora) => horasDisponibles.includes(hora));
                  const estaSeleccionada = horasSeleccionadas.includes(horaSlot);

                  let estadoClase = 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed';
                  if (estaReservada) {
                    estadoClase = 'border-rose-200 bg-rose-50 text-rose-700 cursor-not-allowed';
                  } else if (disponible && estaSeleccionada) {
                    estadoClase = 'border-primary bg-green-50 text-primary';
                  } else if (estaPendiente && disponible) {
                    estadoClase = 'border-blue-200 bg-blue-50 text-blue-700 hover:border-primary';
                  } else if (estaPendiente) {
                    estadoClase = 'border-blue-200 bg-blue-50 text-blue-700 cursor-not-allowed';
                  } else if (disponible && bloqueDisponible) {
                    estadoClase = 'border-gray-200 hover:border-primary hover:bg-green-50';
                  }

                  return (
                    <button
                      key={horaSlot}
                      type="button"
                      onClick={() => {
                        if (!disponible) return;

                        if (!bloqueDisponible) {
                          setMensaje({
                            tipo: 'error',
                            texto: 'Elige una hora inicial que tenga suficiente disponibilidad para tu duración.'
                          });
                          return;
                        }

                        setFormData({ ...formData, hora: horaSlot });
                      }}
                      disabled={!disponible || !bloqueDisponible}
                      className={`p-3 rounded-lg border text-center font-semibold transition ${estadoClase}`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{horaSlot}</span>
                        {!disponible && (
                          <span className="text-xs font-medium uppercase tracking-wide">
                            {estaReservada ? 'Reservado' : estaPendiente ? 'Pendiente' : 'No disponible'}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            {(horarioDelDia.horas || []).filter((horaSlot) => horasDisponibles.includes(horaSlot) && !horasOcupadas.includes(horaSlot)).length === 0 &&
              !consultando && (
                <p className="text-sm text-red-600 mt-2">No hay horarios disponibles para esta fecha.</p>
              )}

            {horasPendientes.length > 0 && (
              <div className="mt-6">
                <p className="text-sm text-gray-500 mb-2">Horarios pendientes de aprobación:</p>
                <div className="flex flex-wrap gap-2">
                  {horasPendientes.map((hora) => (
                    <span key={hora} className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm">
                      {hora}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {horasOcupadas.length > 0 && (
              <div className="mt-6">
                <p className="text-sm text-gray-500 mb-2">Horarios ocupados:</p>
                <div className="flex flex-wrap gap-2">
                  {horasOcupadas.map((hora) => (
                    <span key={hora} className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm">
                      {hora}
                    </span>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        <div className="mt-12 rounded-[1.75rem] bg-white p-6 shadow-sm md:p-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Disponibilidad por día</p>
              <h3 className="text-2xl font-semibold text-gray-800">Próximos 7 días</h3>
            </div>
            {cargandoDias && <span className="text-sm text-gray-500">Actualizando calendario...</span>}
          </div>

          {errorDias ? (
            <div className="p-4 rounded-lg bg-red-50 text-red-700 border border-red-200">{errorDias}</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {disponibilidadDias.map((dia) => (
                <div
                  key={dia.fechaValor}
                  className={`rounded-xl border shadow-sm p-4 ${
                    esFestivoColombia(dia.fechaValor) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs uppercase text-gray-500">{dia.fechaValor}</p>
                      <p className="text-lg font-semibold text-gray-800">{dia.etiqueta}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {esFestivoColombia(dia.fechaValor) && (
                        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                          Festivo
                        </span>
                      )}
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          dia.error
                            ? 'bg-red-100 text-red-700'
                            : dia.horasDisponibles.length > 0
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {dia.error
                          ? 'Sin reservas'
                          : dia.horasDisponibles.length > 0
                            ? `${dia.horasDisponibles.length} horarios libres`
                            : 'Sin reservas'}
                      </span>
                    </div>
                  </div>

                  {dia.error ? (
                    <p className="text-sm text-red-700">Sin reservas</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {(dia.horario?.horas || DEFAULT_HORARIO.horas).map((hora) => {
                        const estaReservada = dia.horasOcupadas?.includes(hora);
                        const estaPendiente = dia.horasPendientes?.includes(hora);
                        const disponible = dia.horasDisponibles.includes(hora);
                        const ocupado = estaReservada || estaPendiente || !disponible;
                        
                        return (
                          <div
                            key={`${dia.fechaValor}-${hora}`}
                            className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                              estaReservada
                                ? 'border-rose-200 bg-rose-50 text-rose-700'
                                : estaPendiente
                                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                                  : ocupado
                                  ? 'border-gray-200 bg-gray-50 text-gray-600'
                                  : 'border-green-200 bg-green-50 text-green-800'
                            }`}
                          >
                            <span className="font-semibold">{hora}</span>
                            <span className="text-xs uppercase tracking-wide">
                              {estaReservada ? 'Reservado' : estaPendiente ? 'Pendiente' : ocupado ? 'No disponible' : 'Disponible'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
            <div className="mt-10 md:hidden">
          <Link
            to="/login"
            className="block w-full text-center px-4 py-3 rounded-lg bg-white text-primary font-semibold border border-primary shadow-sm hover:bg-primary hover:text-white transition"
          >
            Acceso para administración
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;
